import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui';
import type { Operation } from '@budgira/shared';

interface DuplicateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (paymentDate: string, amount: number) => void;
  operation: Operation | null;
}

export const DuplicateModal = ({ isOpen, onClose, onConfirm, operation }: DuplicateModalProps) => {
  const { t } = useTranslation();
  const [paymentDate, setPaymentDate] = useState('');
  const [amount, setAmount] = useState('');

  // Reset values when modal opens
  useEffect(() => {
    if (isOpen && operation) {
      setPaymentDate(new Date().toISOString().split('T')[0] || '');
      setAmount(operation.amount.toString());
    }
  }, [isOpen, operation]);

  if (!isOpen || !operation) return null;

  const handlePreviousDay = () => {
    const date = new Date(paymentDate);
    date.setDate(date.getDate() - 1);
    setPaymentDate(date.toISOString().split('T')[0] || '');
  };

  const handleNextDay = () => {
    const date = new Date(paymentDate);
    date.setDate(date.getDate() + 1);
    setPaymentDate(date.toISOString().split('T')[0] || '');
  };

  const handleConfirm = () => {
    const parsedAmount = parseFloat(amount);
    if (!isNaN(parsedAmount) && parsedAmount > 0) {
      onConfirm(paymentDate, parsedAmount);
    }
  };

  const isValid = paymentDate && amount && parseFloat(amount) > 0;

  // Format counterparty name for display
  const counterpartyName = operation.counterparty?.name || t('operations.unknownCounterparty', 'Inconnu');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50" 
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
          {t('operations.duplicateModal.title', 'Dupliquer l\'opération')}
        </h3>
        
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-4 truncate">
          {counterpartyName} - {operation.amount.toFixed(2)} €
        </p>
        
        {/* Payment Date */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            {t('operations.duplicateModal.paymentDate', 'Date de paiement')}
          </label>
          
          <div className="flex items-center gap-2">
            {/* Previous day button */}
            <button
              type="button"
              onClick={handlePreviousDay}
              className="p-2 rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              title={t('operations.duplicateModal.previousDay', 'Jour précédent')}
            >
              <svg className="w-5 h-5 text-slate-600 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            {/* Date input */}
            <input
              type="date"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              className="flex-1 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 text-center focus:ring-2 focus:ring-violet-500 focus:border-transparent"
            />
            
            {/* Next day button */}
            <button
              type="button"
              onClick={handleNextDay}
              className="p-2 rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
              title={t('operations.duplicateModal.nextDay', 'Jour suivant')}
            >
              <svg className="w-5 h-5 text-slate-600 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
        
        {/* Amount */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            {t('operations.duplicateModal.amount', 'Montant')}
          </label>
          
          <div className="relative">
            <input
              type="number"
              step="0.01"
              min="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full px-3 py-2 pr-8 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-violet-500 focus:border-transparent"
              placeholder="0.00"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500">
              €
            </span>
          </div>
        </div>
        
        {/* Buttons */}
        <div className="flex gap-3 justify-end">
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel', 'Annuler')}
          </Button>
          <Button onClick={handleConfirm} disabled={!isValid}>
            {t('operations.duplicateModal.confirm', 'Dupliquer')}
          </Button>
        </div>
      </div>
    </div>
  );
};
